/**
 * Calculate profile completeness percentage for a promoter
 * Returns a percentage (0-100) and details of completed/missing fields
 */
export const calculateProfileCompleteness = (promoter) => {
  if (!promoter) return { percentage: 0, completed: [], missing: [], total: 0 };

  const fields = [
    // Basic Info (Required)
    { key: "first_name", label: "First Name", weight: 5 },
    { key: "last_name", label: "Last Name", weight: 5 },
    { key: "email", label: "Email", weight: 5 },
    { key: "phone_number", label: "Phone Number", weight: 5 },
    { key: "account_display_name", label: "Display Name", weight: 3 },
    
    // Verification (Critical)
    { key: "email_verified_at", label: "Email Verification", weight: 10 },
    { key: "phone_number_verified_at", label: "Phone Verification", weight: 10 },
    
    // Address Info
    { key: "address.street_address_1", label: "Street Address", weight: 3 },
    { key: "address.city", label: "City", weight: 3 },
    { key: "address.region", label: "Region", weight: 3 },
    { key: "address.country", label: "Country", weight: 3 },
    
    // ID Information
    { key: "id_type", label: "ID Type", weight: 5 },
    { key: "id_number", label: "ID Number", weight: 5 },
    
    // Promotion Channels (Critical)
    { key: "promotion_channels", label: "Promotion Channels", weight: 10, isObject: true },
    
    // Content & Audience Info
    { key: "content_categories", label: "Content Categories", weight: 5, isArray: true },
    { key: "target_audiences", label: "Target Audiences", weight: 3, isArray: true },
    { key: "posting_frequency", label: "Posting Frequency", weight: 3 },
    { key: "weekly_post_count", label: "Weekly Post Count", weight: 2 },
    
    // Experience & Motivation
    { key: "has_affiliate_experience", label: "Affiliate Experience", weight: 2 },
    { key: "motivation_to_join", label: "Motivation", weight: 3 },
    
    // Compliance Agreements
    { key: "agree_performance_based", label: "Performance Agreement", weight: 2 },
    { key: "agree_no_spam", label: "No Spam Agreement", weight: 2 },
    { key: "agree_follow_rules", label: "Rules Agreement", weight: 2 },
    { key: "agree_accept_consequences", label: "Consequences Agreement", weight: 2 },
  ];

  const getValue = (obj, path) => {
    return path.split(".").reduce((acc, part) => acc && acc[part], obj);
  };

  const isFieldComplete = (field) => {
    const value = getValue(promoter, field.key);
    
    if (field.isArray) {
      return Array.isArray(value) && value.length > 0;
    }
    
    if (field.isObject) {
      if (typeof value === "object" && value !== null) {
        // For promotion_channels, check if at least one channel exists
        const channels = Object.values(value).filter(ch => ch && Array.isArray(ch) && ch.length > 0);
        return channels.length > 0;
      }
      return false;
    }
    
    if (typeof value === "boolean") {
      return value === true;
    }
    
    return value !== null && value !== undefined && value !== "";
  };

  let totalWeight = 0;
  let completedWeight = 0;
  const completed = [];
  const missing = [];

  fields.forEach((field) => {
    totalWeight += field.weight;
    if (isFieldComplete(field)) {
      completedWeight += field.weight;
      completed.push(field.label);
    } else {
      missing.push(field.label);
    }
  });

  const percentage = Math.round((completedWeight / totalWeight) * 100);

  return {
    percentage,
    completed,
    missing,
    total: fields.length,
    completedCount: completed.length,
    missingCount: missing.length,
  };
};

/**
 * Count promotion channels from the new data structure
 */
export const countPromotionChannels = (promotion_channels) => {
  if (!promotion_channels || typeof promotion_channels !== "object") {
    return 0;
  }

  let count = 0;
  Object.values(promotion_channels).forEach((channelArray) => {
    if (Array.isArray(channelArray)) {
      count += channelArray.length;
    }
  });

  return count;
};

/**
 * Calculate total audience size across all promotion channels
 */
export const calculateTotalAudienceSize = (promotion_channels) => {
  if (!promotion_channels || typeof promotion_channels !== "object") {
    return 0;
  }

  let total = 0;

  Object.values(promotion_channels).forEach((channelArray) => {
    if (Array.isArray(channelArray)) {
      channelArray.forEach((channel) => {
        const audienceSize = parseAudienceSize(channel.audienceSize);
        total += audienceSize;
      });
    }
  });

  return total;
};

/**
 * Parse audience size string to number
 */
export const parseAudienceSize = (audienceSize) => {
  if (!audienceSize) return 0;
  
  const str = audienceSize.toString().toUpperCase();
  
  if (str.includes("M")) {
    return parseFloat(str) * 1000000;
  } else if (str.includes("K")) {
    return parseFloat(str) * 1000;
  }
  
  return parseFloat(str) || 0;
};

/**
 * Format audience size for display
 */
export const formatAudienceSize = (size) => {
  const num = typeof size === "string" ? parseAudienceSize(size) : size;
  
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  } else if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  
  return num.toString();
};
